import type { AutocompleteChangeDetails, AutocompleteChangeReason } from '@mui/material';
import type { ISelection } from 'app/ComponentLibrary/Filter/CustomSelect';

/**
 * Shared `/search` transport for “blank only” order filters (WI 498879, WI 504999).
 * Contract:
 * - Blank-enabled picklists use `null` sentinel values in id arrays (e.g. `regionIds: [null]`).
 * - Order start / end date blank state uses explicit booleans:
 *   `orderStartDateRangeIsBlank: true` and `endDateRangeIsBlank: true`.
 * - Order bill rate “blank only”: `orderBillRateIsBlank: true` with `orderBillRate` omitted/null.
 *
 * - Multi-select “blank only”: request field is a JSON array containing a single `null`
 *   (e.g. `"regionIds":[null]`).
 * - Order start / end date “blank only”: companion booleans `orderStartDateRangeIsBlank` and
 *   `endDateRangeIsBlank` are set to `true` while the corresponding date range object is omitted.
 */
export const ORDER_SEARCH_BLANK_TOKEN = '__ORDER_SEARCH_BLANK__';

export const orderSearchBlankSelection = (): ISelection =>
  ({
    id: ORDER_SEARCH_BLANK_TOKEN,
    name: 'Blank',
    value: ORDER_SEARCH_BLANK_TOKEN,
  }) as ISelection;

export const prependOrderSearchBlankOption = <T extends { value?: unknown }>(options: T[] | null | undefined): T[] => {
  const blank = orderSearchBlankSelection() as unknown as T;
  const rest = (options || []).filter(o => o?.value !== ORDER_SEARCH_BLANK_TOKEN);
  return [blank, ...rest];
};

/** Exclude synthetic Blank from “Select all” so it never combines with every real id. */
export const excludeOrderSearchBlankFromOptions = <T extends { value?: unknown }>(options: T[] | null | undefined) =>
  (options || []).filter(o => o?.value !== ORDER_SEARCH_BLANK_TOKEN);

/**
 * Selecting Blank clears other values; selecting any real value clears Blank.
 * “Select all” must not include Blank (handled via `applySelectAllFilter` on TypeAheadDropdown).
 */
export const orderSearchBlankMultiSelectHandleChange = (
  choice: ISelection[] | null,
  _reason: AutocompleteChangeReason,
  details: AutocompleteChangeDetails<ISelection>,
  onChange: (v: ISelection[] | null) => void,
) => {
  if (!Array.isArray(choice)) {
    onChange(choice);
    return;
  }
  const blank = orderSearchBlankSelection();
  const clicked = details?.option as ISelection | undefined;
  const hasBlank = (arr: ISelection[]) => arr?.some(i => i?.value === ORDER_SEARCH_BLANK_TOKEN);

  if (clicked?.value === ORDER_SEARCH_BLANK_TOKEN) {
    if (hasBlank(choice)) {
      onChange([blank]);
      return;
    }
  }
  if (hasBlank(choice) && clicked?.value !== ORDER_SEARCH_BLANK_TOKEN) {
    onChange(choice.filter(i => i?.value !== ORDER_SEARCH_BLANK_TOKEN));
    return;
  }
  onChange(choice);
};

export const mapOrderSearchBlankTokenToNull = (v: unknown) => (v === ORDER_SEARCH_BLANK_TOKEN ? null : v);
